import { useMutation, useQuery } from "@apollo/client"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import React, { useState } from "react"
import { shoppingItemsDoc } from "./shopping"
import { graphql } from "@/api"
import Modal from "@/common/components/modal"
import env from "@/common/env"
import GoogleIcon from "@/images/layout/google.png"
import SpinnerIcon from "@/images/layout/spinner.gif"

const shoppingSyncUpdateDoc = graphql(`
  mutation ShoppingSyncUpdate($id: ID!, $synced: Boolean!) {
    shoppingSyncUpdate(id: $id, synced: $synced) {
      shoppingSync
    }
  }
`)

const signExamplesShoppingSyncDoc = graphql(`
  query SignSignTags($id: ID!) {
    sign(id: $id) {
      id

      examples {
        id
        shoppingSync

        signTag {
          id
          locale
          pagename
        }
      }
    }
  }
`)

interface SyncProps {
  exampleId: string
  exampleableId: string
  openForm: () => void
}

const Sync: React.FC<SyncProps> = ({ exampleId, exampleableId, openForm }) => {
  const {
    data: signExamplesData,
    error: signExamplesDataError,
    loading: signExamplesDataLoading,
  } = useQuery(signExamplesShoppingSyncDoc, {
    variables: { id: exampleableId },
  })

  const { data: shoppingItemsData } = useQuery(shoppingItemsDoc, {
    variables: { id: exampleId },
  })

  const examples = signExamplesData?.sign?.examples
  const example = signExamplesData?.sign?.examples.find(example => example.id === exampleId)
  const signTagTitles = examples
    ?.filter(example => example.signTag.locale === env.locale)
    .map(example => example.signTag.pagename)

  const [syncUpdateMutation, { loading: syncUpdateLoading }] = useMutation(shoppingSyncUpdateDoc)
  const [updateSyncModalOpen, setUpdateSyncModalOpen] = useState(false)

  const updateSync = () => {
    try {
      if (shoppingItemsData?.shoppingItems.some(item => item.title.length === 0 || item.description.length === 0)) {
        alert("You have not entered a title or description.")
        return
      }
    } catch {
      // noop
    }

    syncUpdateMutation({
      variables: {
        id: exampleId,
        synced: !example?.shoppingSync,
      },
      update: (store, { data }) => {
        if (data?.shoppingSyncUpdate) {
          const existingData = store.readQuery({
            query: signExamplesShoppingSyncDoc,
            variables: { id: exampleableId },
          })

          if (existingData && existingData.sign && existingData.sign.examples) {
            const shoppingSync = data.shoppingSyncUpdate?.shoppingSync
            const updatedExamples = existingData.sign.examples.map(example => {
              if (example.id === exampleId) {
                return {
                  ...example,
                  shoppingSync: shoppingSync,
                }
              }
              return example
            })

            store.writeQuery({
              query: signExamplesShoppingSyncDoc,
              variables: { id: exampleableId },
              data: { ...existingData, sign: { ...existingData.sign, examples: updatedExamples } },
            })

            if (shoppingSync) {
              openForm()
            }
          }
        }
      },
    })
  }

  const handleUpdateSync = () => {
    const shoppingItemsCount = (example?.shoppingSync && shoppingItemsData?.shoppingItems?.length) || 0
    const signTagTitlesCount = signTagTitles?.length || 0

    if (shoppingItemsCount > 0 && signTagTitlesCount > 0) {
      setUpdateSyncModalOpen(true)
    } else updateSync()
  }

  const disabled = useSSRDisabled()

  if (signExamplesDataError) {
    return (
      <label className="mt-1" htmlFor={`shoppingbox-${exampleId}-datasync`}>
        <img className="w-4" src={GoogleIcon} />
      </label>
    )
  }

  return (
    <>
      <label className="mt-1" htmlFor={`shoppingbox-${exampleId}-datasync`}>
        <img className="w-4" src={GoogleIcon} />
      </label>
      {signExamplesDataLoading || syncUpdateLoading || disabled ? (
        <img src={SpinnerIcon} />
      ) : (
        <div className="relative">
          <div className="checkbox !pt-0">
            <input
              type="checkbox"
              id={`shoppingbox-${exampleId}-datasync`}
              name={`shoppingbox-${exampleId}-datasync`}
              className="form-control datasync"
              checked={example?.shoppingSync || false}
              onChange={() => handleUpdateSync()}
            />
            <label htmlFor={`shoppingbox-${exampleId}-datasync`} className="checkbox-label paragraph-xs !pl-5">
              <span className="sr-only">Update Sync</span>
            </label>
          </div>
        </div>
      )}

      <Modal
        width="600px"
        className="update-sync-modal"
        open={updateSyncModalOpen}
        setOpen={setUpdateSyncModalOpen}
        padding="small"
      >
        <Modal.Body className="success flex">
          <div className="text">
            <h4 className="mb-2 pr-10">{`Sign ${exampleId} will be disassociated from google shopping on the page(s):`}</h4>
            {signTagTitles?.map(title => (
              <p key={title} className="paragraph-sm mb-0">
                {title}
              </p>
            ))}
            <div className="mt-2 flex items-center justify-between">
              <button
                type="button"
                className="button button-small button-secondary mb-0"
                onClick={() => setUpdateSyncModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button button-small button-primary mb-0"
                onClick={() => {
                  updateSync()
                  setUpdateSyncModalOpen(false)
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default Sync
